import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCategory,
  createCategory,
  updateCategory,
  deleteCategory
} from '../../Redux/Reducers/CategoryReducer';
import { uploadImagecertifates } from '../../Redux/Actions/Action';
import {
  Box, IconButton, Dialog, DialogActions, DialogContent,
  DialogTitle, Button, TablePagination, CircularProgress,
  TextField, Grid, Divider, Typography, Tooltip, Avatar,
  DialogContentText, Switch
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BsDownload } from 'react-icons/bs';
import * as XLSX from 'xlsx';


const EMPTY_FORM = { categoryName: '', icon: '' };

function CategoryList() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialPageSize = parseInt(searchParams.get('pageSize') || '10', 10);
  const initialSearch = searchParams.get('search') || '';

  const [paginationModel, setPaginationModel] = useState({
    page: Number.isFinite(initialPage) && initialPage >= 0 ? initialPage : 0,
    pageSize: Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : 10,
  });
  const [searchText, setSearchText] = useState(initialSearch);
  const [appliedSearch, setAppliedSearch] = useState(initialSearch);
  const [exportLoading, setExportLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  // Dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [iconFile, setIconFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const didInitUrlSyncRef = useRef(false);

  const { category = [], pagination, loading: categoryLoading } =
    useSelector((state) => state?.CategoryReducer || {});

  // Fetch list
  const fetchList = useCallback(() => {
    const { page, pageSize } = paginationModel;
    let limit = Number(pageSize);
    if (isNaN(limit)) limit = 10;
    dispatch(fetchCategory({ page: page + 1, limit, search: appliedSearch }));
  }, [dispatch, paginationModel.page, paginationModel.pageSize, appliedSearch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // URL sync
  useEffect(() => {
    if (!didInitUrlSyncRef.current) { didInitUrlSyncRef.current = true; return; }
    const params = new URLSearchParams();
    params.set('page', String(paginationModel.page));
    params.set('pageSize', String(paginationModel.pageSize));
    if (appliedSearch) params.set('search', appliedSearch);
    setSearchParams(params, { replace: true });
  }, [paginationModel.page, paginationModel.pageSize, appliedSearch, setSearchParams]);

  const handleOpenCreate = () => {
    setEditId(null);
    setEditForm(EMPTY_FORM);
    setIconFile(null);
    setEditOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditId(row.id);
    setEditForm({
      categoryName: row.categoryName || '',
      icon: row.icon || '',
    });
    setIconFile(null);
    setEditOpen(true);
  };

  const handleEditChange = (field, value) =>
    setEditForm((prev) => ({ ...prev, [field]: value }));

  const handleIconChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setIconFile(file);
      setEditForm((prev) => ({ ...prev, icon: URL.createObjectURL(file) }));
    }
  };

  const handleEditSubmit = async () => {
    if (!editForm.categoryName.trim()) { toast.error('Category Name is required'); return; }

    setActionLoading((prev) => ({ ...prev, edit: true }));
    try {
      let iconUrl = editForm.icon;

      if (iconFile) {
        setUploading(true);
        const uploadedUrl = await dispatch(uploadImagecertifates(iconFile));
        setUploading(false);
        if (!uploadedUrl) {
          toast.error("Failed to upload image");
          setActionLoading((prev) => ({ ...prev, edit: false }));
          return;
        }
        iconUrl = typeof uploadedUrl === 'string' ? uploadedUrl : uploadedUrl[0];
      }

      const payload = {
        categoryName: editForm.categoryName,
        icon: iconUrl,
      };

      if (editId) {
        await dispatch(updateCategory({ id: editId, payload })).unwrap();
        toast.success('Category updated successfully!');
      } else {
        await dispatch(createCategory(payload)).unwrap();
        toast.success('Category created successfully!');
      }
      setEditOpen(false);
      setEditForm(EMPTY_FORM);
      setIconFile(null);
      fetchList();
    } catch (err) {
      toast.error(err || `Failed to ${editId ? 'update' : 'create'} category`);
    } finally {
      setActionLoading((prev) => ({ ...prev, edit: false }));
      setUploading(false);
    }
  };

  const handleOpenDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const handleDeleteSubmit = async () => {
    if (!deleteId) return;
    setActionLoading((prev) => ({ ...prev, delete: true }));
    try {
      await dispatch(deleteCategory(deleteId)).unwrap();
      toast.success('Category deleted successfully!');
      setDeleteOpen(false);
      setDeleteId(null);
      fetchList();
    } catch (err) {
      toast.error(err || 'Failed to delete category');
    } finally {
      setActionLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const handleChangePage = (e, newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage }));
  const handleChangeRowsPerPage = (e) => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) });

  const handleApplySearch = () => { setAppliedSearch(searchText.trim()); setPaginationModel((prev) => ({ ...prev, page: 0 })); };
  const handleResetSearch = () => { setSearchText(''); setAppliedSearch(''); setPaginationModel({ page: 0, pageSize: 10 }); };

  const handleExport = useCallback(async () => {
    const token = localStorage.getItem('astroVani-admin-token');
    setExportLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASEURL}/admin/category?${appliedSearch ? `&search=${encodeURIComponent(appliedSearch)}` : ''}`, {
        headers: { Authorization: token },
      });
      if (!res.ok) throw new Error('Export failed');
      const result = await res.json();
      const categories = Array.isArray(result) ? result : result?.data || [];
      if (!categories.length) { toast.error('No data to export'); return; }

      const headers = ['Category Name', 'Icon URL', 'Created At'];
      const exportRows = categories.map((c) => [
        c.categoryName || '',
        c.icon || '',
        c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '',
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...exportRows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Categories');
      ws['!cols'] = headers.map((h) => ({ wch: h.length + 15 }));
      XLSX.writeFile(wb, 'categories_export.xlsx');
      toast.success('Exported successfully!');
    } catch {
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  }, [appliedSearch]);
  const handleToggleCategory = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('astroVani-admin-token');
      const res = await fetch(`${process.env.REACT_APP_BASEURL}/admin/toggleCategory/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Category status updated successfully!');
        fetchList(); // Refresh list to get updated status
      } else {
        toast.error(data.message || 'Failed to update category status');
      }
    } catch (error) {
      toast.error('An error occurred while updating category status');
    }
  };

  const columns = [
    { field: 'sno', headerName: 'S.No', width: 65, filterable: false, sortable: false, headerAlign: 'center', align: 'center' },
    {
      field: 'icon', headerName: 'Icon', width: 90, headerAlign: 'center', align: 'center', sortable: false, filterable: false,
      renderCell: ({ row }) => (
        row.icon ? <Avatar src={row.icon} variant="rounded" sx={{ width: 32, height: 32 }} /> : <Avatar variant="rounded" sx={{ width: 32, height: 32 }}>C</Avatar>
      ),
    },
    {
      field: 'categoryName', headerName: 'Category Name', flex: 1, minWidth: 200,
      renderCell: (params) => <span style={{ fontWeight: 500 }}>{params.value || '-'}</span>,
    },
    {
      field: 'isActive', headerName: 'Status', width: 100, headerAlign: 'center', align: 'center', sortable: false, filterable: false,
      renderCell: ({ row }) => (
        <Switch
          checked={row.isActive || false}
          onChange={() => handleToggleCategory(row.id, row.isActive)}
          color="primary"
          size="small"
        />
      ),
    },
    {
      field: 'createdAt', headerName: 'Created At', width: 180, type: 'dateTime',
      valueFormatter: (value) => value ? new Date(value).toLocaleString() : '-',
    },
    {
      field: 'actions', headerName: 'Actions', width: 120, sortable: false, filterable: false, headerAlign: 'center', align: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <Tooltip title="Edit Category">
            <IconButton size="small" onClick={() => handleOpenEdit(row)}
              sx={{ color: '#1976d2', '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' } }}>
              <i className="icofont-edit" style={{ fontSize: '17px' }}></i>
            </IconButton>
          </Tooltip>
          { (row?.id !== '6a69cccddc1a587997197f52' && row?.isSpell !== true) && 
          <Tooltip title="Delete Category">
            <IconButton size="small" onClick={() => handleOpenDelete(row.id)}
              sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211,47,47,0.08)' } }}>
              <i className="icofont-trash" style={{ fontSize: '17px' }}></i>
            </IconButton>
          </Tooltip> }
        </Box>
      ),
    },
  ];

  const rows = (category || []).map((item, index) => {
    if (!item || !item._id) return null;
    return {
      sno: paginationModel.page * paginationModel.pageSize + index + 1,
      id: item._id,
      categoryName: item.categoryName || '-',
      icon: item.icon || '',
      isActive: item.isActive,
      createdAt: item.createdAt ? new Date(item.createdAt) : null,
    };
  }).filter(Boolean);

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Pooja Categories" />

        <div className="card mb-3" style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none', padding: '20px' }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" sx={{ gap: 2 }}>
            <Box sx={{ flex: '2 1 220px' }}>
              <label className="form-label fw-bold small text-muted mb-1">Search</label>
              <input type="text" placeholder="Search by category name..." value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplySearch(); }}
                className="form-control form-control-sm" />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <Button variant="contained" onClick={handleApplySearch} size="small" sx={{ textTransform: 'none', height: '31px' }}>Search</Button>
              <Button variant="outlined" onClick={handleResetSearch} disabled={!appliedSearch && !searchText} size="small" sx={{ textTransform: 'none', height: '31px' }}>Reset</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <Button variant="contained"
                startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <BsDownload />}
                size="small" disabled={exportLoading || categoryLoading} onClick={handleExport}
                sx={{ fontSize: '13px', px: 2, py: 0.75, borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }}>
                {exportLoading ? 'Exporting...' : 'Export'}
              </Button>
              <Button variant="contained" onClick={handleOpenCreate} size="small"
                sx={{ fontSize: '13px', px: 2, py: 0.75, borderRadius: '8px', textTransform: 'none', boxShadow: 'none', backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#6a1b9a' } }}>
                + Add Category
              </Button>
            </Box>
          </Box>
        </div>

        <div className="row g-0">
          <div className="col-md-12">
            <div className="card" style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none', overflow: 'hidden' }}>
              <div className="card-body" style={{ padding: 0 }}>
                <Box sx={{ width: '100%' }}>
                  <DataGrid loading={categoryLoading} columns={columns} rows={rows}
                    disableRowSelectionOnClick paginationMode="server" hideFooterPagination
                    autoHeight getRowId={(row) => row.id} sx={{ border: 'none' }} />
                </Box>
                <TablePagination component="div" count={pagination?.totalDocs || 0}
                  page={paginationModel.page} onPageChange={handleChangePage}
                  rowsPerPage={paginationModel.pageSize} onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Rows per page:" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={editOpen} onClose={() => { setEditOpen(false); setEditForm(EMPTY_FORM); setIconFile(null); }}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <i className={editId ? "icofont-edit" : "icofont-plus"} style={{ color: editId ? '#1976d2' : '#7b1fa2', fontSize: '20px' }}></i>
            {editId ? 'Edit Category' : 'Add Category'}
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} display="flex" flexDirection="column" alignItems="center">
              <Box sx={{ position: 'relative', width: 100, height: 100, mb: 2 }}>
                <Avatar
                  src={editForm.icon}
                  variant="rounded"
                  sx={{ width: 100, height: 100, border: '1px dashed #bdbdbd', cursor: 'pointer' }}
                  onClick={() => document.getElementById('icon-upload-input').click()}
                >
                  <i className="icofont-camera fs-2"></i>
                </Avatar>
                <input
                  id="icon-upload-input"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleIconChange}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">Click to upload icon</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Category Name *" value={editForm.categoryName}
                onChange={(e) => handleEditChange('categoryName', e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setEditOpen(false); setEditForm(EMPTY_FORM); setIconFile(null); }} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px' }} disabled={actionLoading.edit || uploading}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" sx={{ textTransform: 'none', borderRadius: '8px', backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#6a1b9a' } }}
            disabled={actionLoading.edit || uploading}
            startIcon={(actionLoading.edit || uploading) ? <CircularProgress size={16} color="inherit" /> : null}>
            {editId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem', pb: 1, color: '#d32f2f' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <i className="icofont-warning" style={{ fontSize: '20px' }}></i>
            Confirm Delete
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <DialogContentText>
            Are you sure you want to delete this category? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px' }} disabled={actionLoading.delete}>Cancel</Button>
          <Button onClick={handleDeleteSubmit} variant="contained" color="error" sx={{ textTransform: 'none', borderRadius: '8px' }}
            disabled={actionLoading.delete}
            startIcon={actionLoading.delete ? <CircularProgress size={16} color="inherit" /> : null}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default CategoryList;
